import { TextFieldVariants } from '@mui/material';
import { TextField, TextFieldProps } from 'mui-rff';
import { ChangeEvent, KeyboardEvent } from 'react';
import { useField } from 'react-final-form';
import { makeStyles } from 'tss-react/mui';

type IProps = {
  variant: TextFieldVariants;
  name: string;
  format?: (value: any) => any;
  parse?: (value: any) => any;
  onChange?: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
  inputProps?: Record<string, unknown>;
  InputLabelProps?: { shrink?: boolean; [key: string]: unknown };
} & TextFieldProps

const useStyles = makeStyles()((theme) => ({
  outlinedField: {
    '& .MuiInputBase-input': {
      fontSize: '1.25rem',
    },
    '& .MuiInputLabel-root': {
      fontSize: '1.25rem',
    },
    '& .MuiInputLabel-shrink': {
      fontSize: '1rem',
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      '& .MuiOutlinedInput-notchedOutline': {
        borderWidth: '2px',
        borderColor: theme.palette.primary.main,
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderWidth: '2px',
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
        borderWidth: '2px',
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-error .MuiOutlinedInput-notchedOutline': {
        borderWidth: '2px',
        borderColor: theme.palette.error.main,
      },
      '&.Mui-error:hover .MuiOutlinedInput-notchedOutline': {
        borderWidth: '2px',
        borderColor: theme.palette.error.main,
      },
      '&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderWidth: '2px',
        borderColor: theme.palette.error.main,
      },
    },
  },
  helperTextExpanded: {
    display: 'block',
    overflow: 'visible',
    marginTop: theme.spacing(0.375),
    maxHeight: theme.spacing(6),
    opacity: 1,
    transition: 'max-height 240ms ease, margin-top 240ms ease, opacity 240ms ease',
  },
  helperTextCollapsed: {
    display: 'block',
    overflow: 'hidden',
    marginTop: 0,
    maxHeight: '0px',
    opacity: 0,
    transition: 'max-height 240ms ease, margin-top 240ms ease, opacity 240ms ease',
  },
}))

export default function FormControlledTextField({
  variant,
  name,
  helperText,
  showError,
  error,
  slotProps,
  ...props
}: IProps) {
  const { classes } = useStyles();
  const field = useField(name, {
    subscription: {
      error: true,
      submitError: true,
      dirtySinceLastSubmit: true,
      touched: true,
      modified: true,
    },
  });

  const computedShowError = showError
    ? showError({ meta: field.meta })
    : !!(((field.meta.submitError && !field.meta.dirtySinceLastSubmit) || field.meta.error) && (field.meta.touched || field.meta.modified));

  const computedErrorText = computedShowError
    ? (field.meta.error || field.meta.submitError)
    : helperText;

  const resolvedError = error ?? computedShowError;

  const mergedSlotProps = {
    ...slotProps,
    formHelperText: {
      ...(slotProps?.formHelperText || {}),
      sx: {
        mt: 0,
        minHeight: 0,
        overflow: 'visible',
        ...((slotProps?.formHelperText as { sx?: Record<string, unknown> } | undefined)?.sx || {}),
      },
    },
  };

  const helperTextClassName = computedErrorText
    ? classes.helperTextExpanded
    : classes.helperTextCollapsed;

  const animatedHelperText = <span className={helperTextClassName}>{computedErrorText || null}</span>;

  return (
    <>
      <TextField
        {...props}
        className={classes.outlinedField}
        color="primary"
        error={resolvedError}
        helperText={animatedHelperText}
        name={name}
        showError={() => false}
        slotProps={mergedSlotProps}
        variant={variant}
      />
    </>
  )
}